import type { Dispatch, ReactElement, SetStateAction } from "react"
import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useIsMutating, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, CardContent, CardHeader, Grid, IconButton, Skeleton, SvgIcon } from "@mui/material"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import { capitalize } from "lodash"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"

import { KonanAvatar } from "../../../components/core/Avatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { VersioningDialog } from "../../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../../components/status/VersionTag"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateWorkflowRequest } from "../../../types/custom/projects"
import type {
  CloneDataFilesRequest,
  CreateTaglistRequest,
  UpdateTaglistRequest,
  VersionChangeRequest,
} from "../../../types/custom/rules"
import type { DataFileCloneResponse } from "../../../types/generated/api/DataFileCloneResponse"
import type { PaginatedTagListVersionListList } from "../../../types/generated/api/PaginatedTagListVersionListList"
import type { TagListGroupRetrieve } from "../../../types/generated/api/TagListGroupRetrieve"
import type { TagListList } from "../../../types/generated/api/TagListList"
import type { TagListVersionList } from "../../../types/generated/api/TagListVersionList"
import type { TagListVersionRetrieve } from "../../../types/generated/api/TagListVersionRetrieve"
import { WorkflowCreateRequest } from "../../../types/generated/api/WorkflowCreateRequest"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { formatWorkflowComponent } from "../../../utils/WorkflowBlocksHelpers"
import { getValidMaxMinorVersion } from "../../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import { compareTwoRulesList, convertComplexConditionToComponentsGroupConditions } from "../../../utils/rulesetHelpers"
import { updateComponentsDataFilesIds } from "../../ImportFlow/Helpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../../Predictions/Interfaces"
import { ComponentTestingDialog } from "../../Predictions/components/ComponentTestingDialog"
import { groupSchema } from "../../Ruleset"
import { useWorkflowFeatures } from "../../Workflows/utils/workflowHelpers"
import {
  changeTaglistVersion,
  createEmptyCondition,
  createFormikInitialValues,
  handleDeleteRule,
  handleRemoveRule,
  saveImportedTaglistHandler,
} from "../helpers"
import type { TagCardFormikValues, TaglistProps } from "../interface"
import { TagCard } from "./TagCard"

import styles from "../../Ruleset/components/RulesetCard.module.scss"

const levelSchema = Yup.object().shape({
  feature: Yup.string()
    .required(t("TaglistCard.validation.featureRequired", { ns: "taglists" }))
    .matches(/^[^\s]+$/u, {
      message: t("TaglistCard.validation.featureNoSpaces", { ns: "taglists" }),
    }),
  andOr: Yup.string().required(),
  operator: Yup.string().required(t("TaglistCard.validation.operatorRequired", { ns: "taglists" })),
  type: Yup.string().required(t("TaglistCard.validation.operatorRequired", { ns: "taglists" })),
  value: Yup.mixed().when(["valueOrFeature", "type", "operator"], {
    is: (valueOrFeature: string, type: string, operator: string) => {
      return (
        valueOrFeature !== "Feature" &&
        ["true", "false", "null"].includes(type) &&
        ["greater than", "greater than/equal", "lower than", "lower than/equal", "between"].includes(operator)
      )
    },
    then: Yup.number()
      .typeError(t("TaglistCard.validation.valueMustBeNumber", { ns: "taglists" }))
      .required(t("TaglistCard.validation.valueRequired", { ns: "taglists" })),
    otherwise: Yup.mixed(),
  }),
  secondValue: Yup.string().when(["operator"], (operator) => {
    return operator === "between"
      ? Yup.number()
          .typeError(t("TaglistCard.validation.valueMustBeNumber", { ns: "taglists" }))
          .required(t("TaglistCard.validation.valueRequired", { ns: "taglists" }))
      : Yup.string()
  }),
})

// Yup validation schema
// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  name: Yup.string().required(t("TaglistCard.validation.taglistNameRequired", { ns: "taglists" })),
  cards: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required(t("TaglistCard.validation.tagNameRequired", { ns: "taglists" })),
      conditions: Yup.array().of(levelSchema),
      groups: Yup.array().of(groupSchema),
    }),
  ),
})

export function TaglistLoadingComponent(): ReactElement {
  return (
    <Grid item xs={12}>
      <Card className={styles.cardHeader}>
        <CardHeader className={styles.padding} title={<Skeleton width={200} />} />
        <CardContent className={styles.cardContent}>
          <Skeleton height={30} />
          <Skeleton height={30} />
        </CardContent>
      </Card>
    </Grid>
  )
}

/**
 * TaglistCard component for managing taglist creation, editing, and viewing
 *
 * This is the main container component for taglists that handles:
 * - Creating new taglists
 * - Editing existing taglists
 * - Viewing taglist details and versions
 * - Managing tag rules and conditions
 * - Version control and restoration
 * - Import/export functionality
 *
 * The component supports three main modes:
 * - Create mode: For creating new taglists
 * - Edit mode: For modifying existing taglists
 * - View mode: For displaying taglist information
 *
 * @param {TaglistProps} props - Component props
 * @param {string} props.uuid - Unique identifier for the taglist
 * @param {boolean} [props.isAudit=false] - Whether the component is in audit mode
 * @param {boolean} [props.isAllComponentsLoading] - Whether all components are loading
 * @param {function} [props.closeCreateCard] - Function to close create card dialog
 * @param {boolean} [props.isDuplicate] - Whether this is a duplicate taglist
 * @param {function} [props.duplicateTaglist] - Function to duplicate a taglist
 * @param {boolean} [props.disableDuplicateButton] - Whether to disable duplicate button
 * @param {"import"} [props.variant] - Component variant (import mode)
 * @param {function} [props.removeComponent] - Function to remove component
 * @param {function} [props.setTotalNumOfCurrentProjComponents] - Function to set total components count
 * @param {function} [props.setCompletedCardsCounter] - Function to set completed cards counter
 * @param {function} [props.setComponentsErrorMsgs] - Function to set component error messages
 * @param {boolean} [props.isPermittedToCloneFiles] - Whether user can clone files
 * @param {object} [props.allComponentsDataRef] - Reference to all components data
 * @param {object} [props.clonedFilesIdsMap] - Map of cloned file IDs
 * @returns {React.ReactElement} The rendered TaglistCard component
 */
export function TaglistCard(props: Readonly<TaglistProps>): React.ReactElement {
  // Destructure props with default values
  const {
    uuid,
    isAllComponentsLoading,
    closeCreateCard,
    duplicateTaglist,
    removeComponent,
    isDuplicate,
    disableDuplicateButton,
    variant,
    setTotalNumOfCurrentProjComponents,
    setCompletedCardsCounter,
    setComponentsErrorMsgs,
    isPermittedToCloneFiles,
    allComponentsDataRef,
    clonedFilesIdsMap,
    isAudit = false,
    canShowDetails = false,
    metaData,
  } = props

  // Hooks
  const { id: projectId } = useParams<{ id: string }>()
  const { t } = useTranslation("taglists")
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)
  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)
  const theme = getTheme()
  const queryClient = useQueryClient()

  // Permission check constants
  const canDeleteTaglist = useIsPermitted("Delete taglist", permissions.konan, flattenedKonanPermissions)
  const canUpdateTaglist = useIsPermitted("Update taglist", permissions.konan, flattenedKonanPermissions)

  // Get workflow features for condition building
  const features = useWorkflowFeatures(new Set(["SchemaFeatures", "ComputedFeatures", "ConstantFeatures"]))

  // Infinite scroll hook for version pagination
  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isTaglistVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Constants
  const isCreateMode = uuid === "new" || !!isDuplicate
  const emptyCondition = createEmptyCondition()

  // State management
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null)
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)

  // Stores the latest minor version for each major version to determine valid restoration points
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  // Computed values
  const isMenuOpen = Boolean(anchor)

  // Get mutation loading state from react query store
  // This is needed because the component keeps unmounting and mounting
  const isMutating = useIsMutating({
    predicate: (mutation) =>
      mutation.options.mutationKey === "filesClone" + uuid ||
      mutation.options.mutationKey === "componentCreate" + uuid ||
      mutation.options.mutationKey === "workFlowCreate" + uuid,
  })

  // fetch single taglist
  const { isLoading: isTaglistLoading, data: taglist } = useQuery<AxiosResponse<TagListGroupRetrieve>, AxiosError>(
    ["taglist", uuid],
    () => KonanAPI.fetchTaglist(projectId as string, uuid),
    {
      onSuccess: async ({ data }) => {
        const activeVersion = data?.active_version

        await formik.setValues({
          uuid: data.uuid,
          name: `${data.name}${isDuplicate ? t("TaglistCard.labels.copySuffix") : ""}`,
          cards:
            activeVersion?.rules?.map((rule) => {
              const { groups, conditions } = convertComplexConditionToComponentsGroupConditions(
                rule?.condition,
                "Taglist",
              )

              return {
                id: uuidv4(),
                name: rule?.name,
                conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
                groups: groups && groups.length > 0 ? groups : [],
                condition_list_files: [...rule.condition_list_files],
              }
            }) || [],
        })
      },
      enabled: canShowDetails && ((!!projectId && uuid !== "new" && variant !== "import") || isDuplicate),
    },
  )

  const {
    isLoading: isTaglistVersionsListLoading,
    isFetchingNextPage,
    data: taglistVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedTagListVersionListList>, AxiosError, TagListVersionList>(
    ["taglistVersionsList", uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listTaglistVersions({
        projectId: projectId as string,
        taglistId: uuid,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<TagListVersionList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as TagListVersionList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen && variant !== "import",
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: taglistVersionData, isLoading: isTaglistVersionDataLoading } = useQuery<
    AxiosResponse<TagListVersionRetrieve>,
    AxiosError
  >(
    ["selectedTaglistVersion", uuid, selectedVersion],
    () =>
      KonanAPI.retrieveTaglistVersion({
        projectId: projectId as string,
        taglistId: uuid as string,
        version: selectedVersion as string,
      }),
    {
      onSuccess: () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          taglistVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(taglistVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen && !!selectedVersion,
    },
  )

  // create workflow mutation
  const createWorkflowMutation = useMutation<AxiosResponse, AxiosError, CreateWorkflowRequest>(
    KonanAPI.createWorkflow,
    {
      mutationKey: "workFlowCreate" + uuid,
      onSuccess: async () => {
        await queryClient.invalidateQueries(["workflows", projectId])
        await queryClient.invalidateQueries(["project", projectId])
      },
    },
  )

  // create taglist
  const createTaglistMutation = useMutation<AxiosResponse, AxiosError, CreateTaglistRequest>(KonanAPI.createTaglist, {
    mutationKey: "componentCreate" + uuid,
    onSuccess: async () => {
      await queryClient.invalidateQueries("taglists")
      await queryClient.invalidateQueries("taglist")
      NotificationUtils.toast(t("TaglistCard.notifications.create.success"), {
        severity: "success",
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (variant === "import") {
        const formattedErrorMessage =
          capitalize(error?.response?.data?.name?.[0]) || t("TaglistCard.notifications.create.errorImportGeneric")

        setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: formattedErrorMessage }))
        return
      }

      if (error?.response?.data?.name) {
        NotificationUtils.toast(error?.response?.data?.name[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.condition) {
        NotificationUtils.toast(error?.response?.data?.condition[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.details) {
        NotificationUtils.toast(error?.response?.data?.details, {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("TaglistCard.notifications.create.errorGeneric"), {
          severity: "error",
        })
      }
    },
  })

  const deleteTaglistMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "taglists" }), {
    onSuccess: async () => {
      NotificationUtils.toast(t("TaglistCard.notifications.delete.success"), { severity: "success" })

      await queryClient.invalidateQueries("taglists")
    },
    onError: ({ response }) => {
      NotificationUtils.toast(response?.data?.details ?? t("TaglistCard.notifications.delete.errorGeneric"), {
        severity: "error",
      })
    },
  })

  // change taglist version
  const taglistVersionChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  // update taglist
  const updateTaglistMutation = useMutation<AxiosResponse, AxiosError, UpdateTaglistRequest>(KonanAPI.updateTaglist, {
    onSuccess: async () => {
      await queryClient.invalidateQueries("taglists")
      await queryClient.invalidateQueries("taglist")
      setIsEditMode(false)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (error?.response?.data?.name) {
        NotificationUtils.toast(error?.response?.data?.name[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.condition) {
        NotificationUtils.toast(error?.response?.data?.condition[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.details) {
        NotificationUtils.toast(error?.response?.data?.details, {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("TaglistCard.notifications.update.errorGeneric"), {
          severity: "error",
        })
      }
    },
  })

  // Clone data file Mutation
  const cloneDataFilesMutation = useMutation<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>(
    KonanAPI.cloneDataFiles,
    {
      mutationKey: "filesClone" + uuid,
      onSuccess: async ({ data }) => {
        if (!clonedFilesIdsMap) return

        data.forEach((file) => {
          clonedFilesIdsMap[file.source.uuid] = file.uuid
        })

        await formik.setValues((prev) => {
          const updatedComponent = updateComponentsDataFilesIds(prev, clonedFilesIdsMap) as TagCardFormikValues

          return { ...updatedComponent }
        })
      },
    },
  )

  // Set the formik initial values according to the variant
  const formikInitialValues = createFormikInitialValues({ uuid, allComponentsDataRef })

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: validationSchema,
    onSubmit: async ({ name, cards }) => {
      const { trimmedName, featuresArray, finalComponentFormat } = formatWorkflowComponent({
        name,
        cards,
      })

      if (workflowsList?.length === 0 && featuresArray?.length > 0) {
        await createWorkflowMutation.mutateAsync({
          name: t("defaults.untitledWorkflow"),
          state: WorkflowCreateRequest.state.DISABLED,
          projectId: projectId as string,
          schema: [...featuresArray],
        })
      }

      if (isCreateMode || variant === "import") {
        // Create a new taglist
        await createTaglistMutation.mutateAsync({
          projectId: projectId as string,
          name: trimmedName,
          rules: [...finalComponentFormat],
        })

        closeCreateCard?.()
        removeComponent?.(uuid)
        setCompletedCardsCounter?.((prev) => ++prev)
      } else {
        if (
          taglist?.data.active_version?.rules &&
          compareTwoRulesList(finalComponentFormat, taglist?.data.active_version?.rules)
        ) {
          await updateTaglistMutation.mutateAsync({
            projectId: projectId as string,
            taglistId: taglist?.data.uuid,
            name: trimmedName === taglist?.data?.name ? undefined : trimmedName,
          })
        } else {
          await updateTaglistMutation.mutateAsync({
            projectId: projectId as string,
            taglistId: taglist?.data.uuid as string,
            name: trimmedName === taglist?.data?.name ? undefined : trimmedName,
            rules: [...(finalComponentFormat ?? [])],
          })
        }

        setIsEditMode(false)
        NotificationUtils.toast(t("TaglistCard.notifications.update.success"), {
          severity: "success",
        })

        await queryClient.invalidateQueries("taglists")
        await queryClient.invalidateQueries("taglist")
      }
    },
  })

  // Effect to sync formik changes with the components ref for Save All functionality
  // This ensures that when the parent component calls Save All, it has the latest form data
  useEffect(() => {
    if (!allComponentsDataRef) return

    allComponentsDataRef.current[uuid] = formik.values
  }, [formik.values, uuid, allComponentsDataRef])

  // Helper function for deleting a rule
  const deleteRule = async (name: string, closeDialogState: Dispatch<SetStateAction<boolean>>): Promise<void> => {
    await handleDeleteRule({
      name,
      closeDialogState,
      values: formik.values,
      updateTaglistMutation,
      projectId: projectId as string,
      taglistId: taglist?.data.uuid ?? "",
      queryClient,
    })

    NotificationUtils.toast(t("TaglistCard.notifications.deleteTag.success"), {
      severity: "success",
    })
  }

  // Helper function for canceling taglist edit
  const handleCancelTaglist = (): void => {
    const activeVersion = taglist?.data?.active_version
    const restoredCards =
      activeVersion?.rules?.map((rule) => {
        const { groups, conditions } = convertComplexConditionToComponentsGroupConditions(rule?.condition, "Taglist")

        return {
          id: uuidv4(),
          name: rule?.name,
          conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
          groups: groups && groups.length > 0 ? groups : [],
          condition_list_files: [...rule.condition_list_files],
        }
      }) || []

    formik.setValues({
      ...formik.values,
      name: taglist?.data?.name ?? formik.values.name,
      cards: restoredCards,
    })

    setIsEditMode(false)
    closeCreateCard?.()
    formik.setErrors({})
  }

  // Helper function for removing a rule
  const handleRemoveRuleWrapper = (ruleIndex: number): boolean => {
    return handleRemoveRule({ isCreateMode, isEditMode, formik, ruleIndex })
  }

  // Helper function for saving imported taglist
  const saveImportedTaglistHandlerWrapper = async (): Promise<void> => {
    await saveImportedTaglistHandler({
      uuid,
      clonedFilesIdsMap,
      setComponentsErrorMsgs,
      formik,
      t,
      cloneDataFilesMutation,
      projectId: projectId as string,
    })
  }

  // helper function for the versioning dialog
  // returns data of the version selected
  const GetTaglistContainer = (): React.ReactElement => {
    const version = taglistVersionData?.data

    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? taglist?.data.active_version?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] ===
        (selectedVersion ?? taglist?.data.active_version?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== taglist?.data.active_version?.version

    const versionFormik = useFormik({
      initialValues: {
        name: formik.values.name,
        cards: version?.rules?.map((rule) => {
          const { conditions, groups } = convertComplexConditionToComponentsGroupConditions(rule?.condition, "Taglist")

          return {
            id: uuidv4(),
            name: rule?.name,
            conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
            groups: groups && groups.length > 0 ? groups : [],
            condition_list_files: [...rule.condition_list_files],
          }
        }),
      },
      onSubmit: () => {
        return
      },
    })

    if (isTaglistVersionsListLoading || isTaglistVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <TaglistLoadingComponent />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {taglist?.data.name}
            </Typography>

            <VersionTag
              version={version?.version ?? selectedVersion}
              color={version ? (taglist?.data.active_version?.version === selectedVersion ? "green" : "blue") : "green"}
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeTaglistVersionWrapper(selectedVersion as string)}
              disabled={taglistVersionChangeMutation.isLoading}
              isLoading={taglistVersionChangeMutation.isLoading}
            >
              {t("TaglistCard.buttons.restore")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0}>
          <FormikProvider value={versionFormik}>
            {version?.rules && 0 < version?.rules?.length ? (
              version?.rules?.map((rule, index: number) => (
                <Grid
                  container
                  item
                  key={rule?.name}
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}
                  pt={1.5}
                >
                  <Grid item xs={12}>
                    <TagCard index={index} andOr={["and", "or"]} key={rule.name} />
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid
                container
                height={"100%"}
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="center"
                pt={1.5}
              >
                <KonanEmptyState
                  title={t("TaglistCard.emptyState.version.noRulesTitle")}
                  subTitle={t("TaglistCard.emptyState.version.noRulesSubtitle")}
                />
              </Grid>
            )}
          </FormikProvider>
        </Grid>
      </Grid>
    )
  }

  // Helper function for changing taglist version
  const changeTaglistVersionWrapper = async (version: string): Promise<void> => {
    try {
      await changeTaglistVersion({
        version,
        taglistVersionChangeMutation,
        projectId: projectId as string,
        uuid,
        queryClient,
      })

      NotificationUtils.toast(t("TaglistCard.notifications.version.restoreSuccess", { version }), {
        severity: "success",
      })
    } catch {
      NotificationUtils.toast(t("TaglistCard.notifications.version.restoreError", { version }), {
        severity: "error",
      })
    }
  }

  const isActionBtnsDisabled = !!isMutating || isAllComponentsLoading

  const selectedTaglist: SelectedComponent = useMemo(
    () => ({
      id: taglist?.data.uuid ?? "",
      type: COMPONENT_TYPES.TAGLIST,
      data: metaData as TagListList,
    }),
    [taglist?.data, metaData],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedTaglist}
        />
      )}

      {isVersionDialogOpen && taglist?.data.active_version && (
        <VersioningDialog
          activeVersion={taglist?.data.active_version}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          isOpen={isVersionDialogOpen}
          versions={taglistVersionsList?.pages || []}
          onClose={() => setIsVersionDialogOpen(false)}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
        >
          <GetTaglistContainer />
        </VersioningDialog>
      )}

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          name={formik.values.name ?? taglist?.data.name}
          onClose={() => setIsDeletionDialogOpen(false)}
          onAccept={() => {
            deleteTaglistMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: uuid,
            })
          }}
          mode={"taglist-deletion"}
          isLoading={deleteTaglistMutation.isLoading}
        />
      )}

      <Card className="card-box-shadow">
        <CardHeader
          className={styles.padding}
          disableTypography
          title={
            <Grid container direction="column" item xs={12} spacing={1}>
              <Grid item container xs={12} justifyContent="space-between">
                <Grid item container xs={6} md={3} display={"flex"} alignItems={"center"}>
                  {isTaglistLoading ? (
                    <Skeleton width={200} />
                  ) : isEditMode || isCreateMode || (variant === "import" && !isActionBtnsDisabled) ? (
                    <InputText
                      hideDescription
                      id="name"
                      placeholder={t("TaglistCard.labels.name")}
                      value={formik.values.name}
                      handleChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
                      handleBlur={formik.handleBlur}
                      disabled={formik.isSubmitting}
                      fullWidth
                    />
                  ) : (
                    <Typography variant="h3-bold" style={{ textTransform: "inherit" }}>
                      {formik.values.name.length ? formik.values.name : metaData?.name}
                    </Typography>
                  )}
                  {!isCreateMode &&
                    !isEditMode &&
                    (taglist?.data.active_version?.version || metaData?.version || selectedVersion) && (
                      <Grid item ml={1} display={"flex"} alignItems={"center"}>
                        <VersionTag
                          version={selectedVersion ?? metaData?.version ?? taglist?.data.active_version.version}
                          color={selectedVersion ? (selectedVersion === metaData?.version ? "green" : "blue") : "green"}
                        />
                      </Grid>
                    )}
                </Grid>

                {!isCreateMode && !isEditMode && variant !== "import" && !isAudit ? (
                  <Grid container item xs justifyContent={"flex-end"}>
                    <IconButton
                      aria-label="settings"
                      size="small"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
                    >
                      <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
                    </IconButton>

                    <Menu
                      key="basic-menu"
                      anchorEl={anchor}
                      open={isMenuOpen}
                      onClose={() => setAnchor(null)}
                      menuMaxContent
                    >
                      <MenuItem
                        onClick={() => {
                          setIsTestDialogOpen(true)
                          setAnchor(null)
                        }}
                        className={styles.optionsMenuItem}
                      >
                        <Typography variant="a" color="base" variantColor={2}>
                          {t("TaglistCard.buttons.test")}
                        </Typography>
                      </MenuItem>
                      {canShowDetails && canDeleteTaglist && (
                        <MenuItem
                          onClick={() => {
                            setIsDeletionDialogOpen(true)

                            setAnchor(null)
                          }}
                        >
                          <Typography variant="a" color="negative" variantColor={2}>
                            {t("TaglistCard.menu.removeTaglist")}
                          </Typography>
                        </MenuItem>
                      )}
                    </Menu>
                  </Grid>
                ) : (
                  variant === "import" &&
                  !isAllComponentsLoading && (
                    <Grid item display={"flex"} gap={1} alignItems={"center"}>
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={isActionBtnsDisabled}
                        onClick={() => {
                          removeComponent?.(uuid)
                          setTotalNumOfCurrentProjComponents?.((prev) => --prev)
                        }}
                        tooltipPlacement="top"
                        tooltip={t("TaglistCard.tooltips.excludeFromImport")}
                      >
                        {t("TaglistCard.buttons.dismiss")}
                      </Button>
                      <Button
                        variant="primary"
                        size="small"
                        disabled={isActionBtnsDisabled || !isPermittedToCloneFiles}
                        onClick={saveImportedTaglistHandlerWrapper}
                        isLoading={isActionBtnsDisabled}
                      >
                        {t("TaglistCard.buttons.save")}
                      </Button>
                    </Grid>
                  )
                )}
              </Grid>

              {/* Avatar and metadata section - Shows creator info and timestamps */}
              <Grid
                item
                container
                xs={12}
                justifyContent="space-between"
                sx={{ flexDirection: { xs: "column", md: "row" } }}
              >
                <Grid container item spacing={1} xs={6} alignItems="flex-start" className={styles.avatarContainer}>
                  <Grid item sx={{ marginTop: "2px" }}>
                    {/* SvgIcon is used to fix square avatars on safari */}
                    <SvgIcon className={styles.modelAvatar}>
                      <KonanAvatar size={24} name={metaData?.created_by ?? taglist?.data.active_version.created_by} />
                    </SvgIcon>
                  </Grid>
                  <Grid container item xs={10}>
                    <Grid item>
                      <Typography
                        variant="label-bold"
                        noWrap
                        style={{ width: "fit-content", marginTop: isCreateMode ? "5px" : "0px" }}
                      >
                        {metaData?.created_by ?? taglist?.data.active_version.created_by}
                      </Typography>

                      {(metaData?.created_at || taglist?.data.active_version.created_at) && (
                        <Typography variant="label-regular" noWrap style={{ width: "fit-content" }}>
                          <Tooltip
                            title={moment(metaData?.created_at ?? taglist?.data.active_version.created_at).format(
                              "DD/MM/YYYY, h:mm A",
                            )}
                            placement="right"
                          >
                            <Typography
                              variant="label-regular"
                              className={styles.date}
                              style={{ width: "fit-content" }}
                            >
                              {moment(metaData?.created_at ?? taglist?.data.active_version.created_at).fromNow()}
                            </Typography>
                          </Tooltip>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Action buttons section - Duplicate, History, Edit/Save/Cancel buttons */}
                <Grid
                  container
                  item
                  xs={12}
                  md={6}
                  alignSelf={"flex-end"}
                  sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}
                  gap={1}
                >
                  {!isCreateMode && !isEditMode && variant !== "import" && !isAudit && (
                    <Fragment>
                      {canShowDetails && (
                        <Grid item>
                          <Button
                            size="small"
                            variant="secondary"
                            disabled={isTaglistLoading || disableDuplicateButton}
                            onClick={() => {
                              duplicateTaglist?.(uuid)
                            }}
                          >
                            {t("TaglistCard.buttons.duplicate")}
                          </Button>
                        </Grid>
                      )}

                      {canShowDetails && (
                        <Grid item>
                          <Button
                            size="small"
                            variant="secondary"
                            disabled={isTaglistLoading}
                            onClick={() => {
                              setSelectedVersion(taglist?.data.active_version?.version)
                              setIsVersionDialogOpen(true)
                            }}
                          >
                            {t("TaglistCard.buttons.history")}
                          </Button>
                        </Grid>
                      )}
                    </Fragment>
                  )}

                  {!isTaglistLoading && !isEditMode && !isCreateMode && variant !== "import"
                    ? canShowDetails &&
                      canUpdateTaglist && (
                        <Grid item>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => {
                              setIsEditMode((prev) => !prev)
                            }}
                          >
                            {t("TaglistCard.buttons.edit")}
                          </Button>
                        </Grid>
                      )
                    : !isTaglistLoading &&
                      (isEditMode || isCreateMode) &&
                      variant !== "import" && (
                        <Grid item display={"flex"} gap={1} alignSelf={"flex-end"}>
                          <Grid item>
                            <Button
                              disabled={isTaglistLoading}
                              size="small"
                              variant={"secondary"}
                              onClick={handleCancelTaglist}
                            >
                              {t("TaglistCard.buttons.cancel")}
                            </Button>
                          </Grid>

                          <Grid item>
                            <Button
                              variant="primary"
                              size="small"
                              disabled={formik.isSubmitting || isTaglistLoading}
                              onClick={formik.submitForm}
                              isLoading={formik.isSubmitting}
                            >
                              {t("TaglistCard.buttons.save")}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                </Grid>
              </Grid>

              {canShowDetails && (
                <Grid item container>
                  <Typography variant="p" display="block" style={{ textTransform: "inherit" }}>
                    {t("TaglistCard.descriptions.card")}
                  </Typography>
                </Grid>
              )}
            </Grid>
          }
        />

        <CardContent className={styles.cardContent}>
          {isTaglistLoading ? (
            <TaglistLoadingComponent />
          ) : canShowDetails ? (
            <Fragment>
              {/* Tag cards section - Renders all tag rules */}
              <Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
                <FormikProvider value={formik}>
                  {formik.values.cards.map((rule, index) => (
                    <Grid key={rule?.id} container item justifyContent="flex-start" alignItems="center" spacing={2}>
                      <Grid item xs={12}>
                        <TagCard
                          createMode={isCreateMode || rule?.id?.includes("new")}
                          editMode={isEditMode}
                          andOr={["and", "or"]}
                          key={`${rule.id}_${features.length}`}
                          index={index}
                          handleRemoveRule={
                            variant === "import" ? undefined : (index: number) => handleRemoveRuleWrapper(index)
                          }
                          handleDelete={(name: string, stateFn: Dispatch<SetStateAction<boolean>>) =>
                            deleteRule(name, stateFn)
                          }
                        />
                      </Grid>
                    </Grid>
                  ))}
                </FormikProvider>
              </Grid>

              {/* Add Tag button section - Only shown in edit/create mode */}
              <Grid container justifyContent="flex-start" alignItems="center" mt={2}>
                {(isEditMode || isCreateMode) && (
                  <Grid item>
                    <Button
                      size="regular"
                      disabled={createTaglistMutation.isLoading || updateTaglistMutation.isLoading}
                      onClick={() => {
                        formik.setFieldValue("cards", [
                          ...formik.values.cards,
                          {
                            id: `new-${uuidv4()}`,
                            name: "",
                            conditions: [emptyCondition],
                            groups: [],
                            condition_list_files: [],
                          },
                        ])
                      }}
                    >
                      {t("TaglistCard.buttons.addTag")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Fragment>
          ) : (
            <Grid
              padding={2}
              border={1}
              borderColor={"var(--base-border-1)"}
              bgcolor={"var(--base-background-1)"}
              borderRadius={1}
            >
              <Typography variant="label-regular" color="base">
                {t("hiddenDuePermission")}
              </Typography>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Fragment>
  )
}
